import { NgFor, NgIf, DecimalPipe, DatePipe, CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceService } from '../../../../Services/Invoice/invoice.service';
import { CustomizeService } from '../../../../Services/Customize_settings/customize.service';

@Component({
  selector: 'app-rough-estimate',
  standalone: true,
  imports: [CommonModule, DecimalPipe],
  templateUrl: './rough-estimate.component.html',
  styleUrl: './rough-estimate.component.css',
  providers: [DecimalPipe]
})
export class RoughEstimateComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private invoiceService = inject(InvoiceService);
  private customizeService = inject(CustomizeService);
  private router = inject(Router);

  isLoading: boolean = false;
  invoiceId!: string;
  invoiceData: any;

  customizeData: any = this.getDefaultSettings();

  productItems: any[] = [];
  packingSummary: any[] = [];
  showStackColumn: boolean = false;

  itemsTotalWeight = 0;
  itemsTotalAmount = 0;
  itemsTotalFineWeight = 0;
  amountWithSilver = 0;
  itemsTotalQty = 0;
  balanceSilverWt: any;

  Number = Number;
  Math = Math;

  ngOnInit() {
    this.invoiceId = this.route.snapshot.paramMap.get('id')!;
    this.getRoughEstimateCustomizeData();
    this.getSingleInvoiceData(this.invoiceId);
  }

  getRoughEstimateCustomizeData() {
    this.customizeService.getCustomizeSettings().subscribe({
      next: (res: any) => {
        if (res.data?.[0]?.rough_estimate_layout_settings) {
          this.customizeData = res.data[0].rough_estimate_layout_settings;
        }
      }
    });
  }

  getSingleInvoiceData(id: string): void {
    this.isLoading = true;

    this.invoiceService.getInvoice(id).subscribe({
      next: (response: any) => {

        const invData = response.data ? response.data : response;
        this.invoiceData = invData;
        this.invoiceData.items = invData.items || [];

        this.balanceSilverWt = Math.abs(Number(this.invoiceData?.balance_silver_wt || 0));

        //  DIRECTLY USE metal_received
        const rateCutMetal = Number(response.metal_received || 0);

        console.log(' metal_received from API:', rateCutMetal);

        this.invoiceData.rateCutMetal = rateCutMetal;

        const rawProducts = this.invoiceData.items.filter(
          (i: any) => i.stout_product_type !== 'packaging'
        );

        this.productItems = this.groupItemsByDescription(rawProducts);

        this.parseRateCutPayload(response.in_rate_cut_payload);

        this.calculateTotals();
        this.buildPackingSummary();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(' Error loading invoice:', err);
        this.isLoading = false;
      }
    });
  }

  private groupItemsByDescription(items: any[]): any[] {
    const groupedMap = new Map<string, any>();

    items.forEach(item => {
      // Group by Item Name or Category Name
      const key = item.stout_item_name || item.category?.name || 'Unknown';

      // Calculate individual quantity (default to 1 if quantity is 0 or null)
      const currentQty = Number(item.stout_quantity) > 0 ? Number(item.stout_quantity) : 1;

      if (groupedMap.has(key)) {
        const existing = groupedMap.get(key);
        existing.stout_total_wt = Number(existing.stout_total_wt || 0) + Number(item.stout_total_wt || 0);
        existing.stout_quantity = Number(existing.stout_quantity || 0) + currentQty;
        existing.stout_fine_weight = Number(existing.stout_fine_weight || 0) + Number(item.stout_fine_weight || 0);
        existing.stout_final_amount = Number(existing.stout_final_amount || 0) + Number(item.stout_final_amount || 0);
        existing.stout_total_amount = Number(existing.stout_total_amount || 0) + Number(item.stout_total_amount || 0);
      } else {
        // Clone and set initial quantity
        const newItem = { ...item };
        newItem.stout_quantity = currentQty;
        groupedMap.set(key, newItem);
      }
    });

    return Array.from(groupedMap.values());
  }

  private parseRateCutPayload(payload: any) {
    if (!payload) return;
    try {
      const data = typeof payload === 'string' ? JSON.parse(payload) : payload;

      // Look for the AMT row in rows array
      if (data.rows && Array.isArray(data.rows)) {
        const amtRow = data.rows.find((r: any) => r.metal === 'AMT' && r.transaction === 'receive');
        if (amtRow) {
          this.invoiceData.rateCutRate = Number(amtRow.rate || 0);
          this.invoiceData.rateCutAmount = Number(amtRow.amount || 0);
          this.invoiceData.rateCutWeight = Number(amtRow.fineWt || amtRow.gsWt || 0);
        }
      }

      // Fallback to top-level fields if amtRow not found or for additional verification
      if (!this.invoiceData.rateCutWeight && data.fixedMetalDelta) {
        this.invoiceData.rateCutWeight = Number(data.fixedMetalDelta || 0);
        this.invoiceData.rateCutAmount = Number(data.cashAmountDelta || 0);
        if (this.invoiceData.rateCutWeight > 0) {
          this.invoiceData.rateCutRate = this.invoiceData.rateCutAmount / this.invoiceData.rateCutWeight;
        }
      }
    } catch (e) {
      console.error('Error parsing rate cut payload', e);
    }
  }

  private calculateTotals() {
    this.itemsTotalWeight = this.invoiceData.items.reduce((s: number, i: any) => s + Number(i.stout_total_wt || 0), 0);
    this.itemsTotalAmount = this.invoiceData.items.reduce((s: number, i: any) => s + Number(i.stout_final_amount || 0), 0);
    this.itemsTotalFineWeight = this.invoiceData.items.reduce((s: number, i: any) => s + Number(i.stout_fine_weight || 0), 0);
    this.amountWithSilver = this.invoiceData.items.reduce((s: number, i: any) => s + Number(i.stout_total_amount || 0), 0);

    // Total Pieces across grouped rows
    this.itemsTotalQty = this.productItems.reduce((s: number, i: any) => s + Number(i.stout_quantity || 0), 0);

    // Calculate Previous Silver Weight with Fallback if database field is 0/null
    let prevMetal = Number(this.invoiceData?.in_previous_silver_wt ?? this.invoiceData?.previous_silver_wt ?? this.invoiceData?.previous_metal ?? 0);
    if (prevMetal === 0 && Number(this.invoiceData?.balance_silver_wt || 0) !== 0) {
      const closingMetal = Number(this.invoiceData?.balance_silver_wt || 0);
      const receivedMetal = Number(this.invoiceData?.rateCutMetal || 0);
      const metalDelta = Number(this.invoiceData?.in_rate_cut_metal_delta || 0);
      prevMetal = closingMetal - this.itemsTotalFineWeight + receivedMetal - metalDelta;
    }
    if (this.invoiceData) {
      this.invoiceData.computed_previous_silver_wt = prevMetal;
    }
  }

  private buildPackingSummary(): void {
    this.packingSummary = [];
    if (!this.invoiceData?.items?.length) return;

    const pkgRows = this.invoiceData.items.filter((i: any) => i.stout_product_type === 'packaging');
    if (!pkgRows.length) return;

    const map: Record<string, number> = {};
    pkgRows.forEach((row: any) => {
      const name = row.minor_name || row.stout_item_name || row.stout_item_code || 'Packaging';
      const minorQty = Number(row.stout_minor_used_qty ?? row.minor_used_qty ?? 0);
      const majorQty = Number(row.stout_major_used_qty ?? row.major_used_qty ?? 0);
      const fallbackQty = Number(row.stout_quantity ?? row.quantity ?? 0);
      const qty = (minorQty > 0 || majorQty > 0) ? (minorQty + majorQty) : (fallbackQty || 1);

      if (qty) map[name] = (map[name] || 0) + qty;
    });

    Object.entries(map).forEach(([name, qty]) => {
      this.packingSummary.push({ minorName: name, quantity: qty });
    });
  }

  printInvoice() {
    const printContents = document.getElementById('print-section')?.innerHTML;
    if (printContents) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  }

  getDefaultSettings() {
    return {
      colors: { themeColor: '#006269', outerBorder: '#006269', totalRowBg: '#9ae3e6', balanceRowBg: '#f3f4f6', packagingHeaderBg: '#f3f4f6' },
      header: {
        mainTitle: { text: 'ROUGH ESTIMATE', visible: true, color: '#006269' },
        voucherLabel: { text: 'Vou.No.:', visible: true, color: '#374151' },
        dateLabel: { text: 'Date:', visible: true, color: '#374151' }
      },
      table: {
        headers: [
          { text: 'S no.', visible: true, color: '#ffffff' }, { text: 'Description', visible: true, color: '#ffffff' },
          { text: 'Net Wt', visible: true, color: '#ffffff' }, { text: 'Tunch', visible: true, color: '#ffffff' },
          { text: 'Pc', visible: true, color: '#ffffff' }, { text: 'Mrp', visible: true, color: '#ffffff' },
          { text: 'Mrp%', visible: true, color: '#ffffff' }, { text: 'Quantity', visible: true, color: '#ffffff' },
          { text: 'Lbr', visible: true, color: '#ffffff' }, { text: 'Fine', visible: true, color: '#ffffff' },
          { text: 'Amount', visible: true, color: '#ffffff' }
        ]
      },
      packaging: {
        visible: true,
        title: { text: 'Packing Details', visible: true, color: '#1f2937' },
        headers: {
          name: { text: 'Minor / Major Name', visible: true, color: '#374151' },
          qty: { text: 'Quantity', visible: true, color: '#374151' }
        },
        emptyMessage: { text: 'No packaging details available.', visible: true, color: '#9ca3af' }
      },
      summary: {
        totalLabel: { text: 'Total', visible: true, color: '#000000' },
        silverValueLabel: { text: 'Silver Value - ₹', visible: true, color: '#000000' },
        metalReceivedLabel: { text: 'Metal Received', visible: true, color: '#000000' },
        previousMetalLabel: { text: 'Previous Metal', visible: true, color: '#dc2626' },
        previousBalanceLabel: { text: 'Previous Balance', visible: true, color: '#dc2626' },
        receiptCashLabel: { text: 'Receipt Cash', visible: true, color: '#000000' },
        closingBalanceLabel: { text: 'Closing Balance', visible: true, color: '#dc2626' }
      },
      footer: {
        termsTitle: { text: 'Terms & Conditions:', visible: true, color: '#000000' },
        termsText: { text: 'NO RETURN NO EXCHANGE', visible: true, color: '#374151' }
      }
    };
  }
}